import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Option, toast } from '@/lib/v2/components';

import { WhatsAppTemplateCreate } from '@/src/infrastructure/Protocol/Inbox/IInboxService';
import { useInboxService } from '@/src/infrastructure/Protocol/Inbox/useInboxService';
import { useUserData } from '@/src/infrastructure/services/useUserData';

import {
  atomIsTemplateCreated,
  atomTemplateOption,
} from '@/modules/CampaignsModule/atoms/actionWhatsAppAtom';
import { TEMPLATE_OPTION } from '@/modules/CampaignsModule/components/actionManagement/WhatsAppAction/constants';
import { TRANSLATE_WHATSAPP_ACTION_FORM } from '@/modules/CampaignsModule/constants';

export interface IWhatsAppTemplateFormV0 {
  title: string;
  message: string;
  header_select: Option;
  header_text: string;
  media_url: string;
  footer: string;
  actions: Array<{ title: string; url: string }>;
  button_type: Option;
  button_title: string;
  button_url: string;
}

enum TEMPLATE_LANGUAGE {
  ENGLISH = 'en',
  PORTUGUESE = 'pt',
  PORTUGUESE_BR = 'pt_BR',
  SPANISH = 'es',
}

const TEMPLATE_LANGUAGE_MAPPING: Record<string, string> = {
  [TEMPLATE_LANGUAGE.PORTUGUESE]: TEMPLATE_LANGUAGE.PORTUGUESE_BR,
  [TEMPLATE_LANGUAGE.SPANISH]: TEMPLATE_LANGUAGE.SPANISH,
  [TEMPLATE_LANGUAGE.ENGLISH]: TEMPLATE_LANGUAGE.ENGLISH,
};

export const useWhatsAppTemplateForm = () => {
  const { t } = useTranslation('inbox');
  const inboxService = useInboxService();
  const [userData] = useUserData();

  const setIsTemplateCreated = useSetAtom(atomIsTemplateCreated);
  const setTemplateOption = useSetAtom(atomTemplateOption);

  const { control, handleSubmit, setValue, getValues, watch, reset } =
    useForm<IWhatsAppTemplateFormV0>();

  const RULES = {
    title: {
      required: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.input`),
      pattern: {
        value: /^[a-z\d_]+$/,
        message: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.title_format`),
      },
      minLength: {
        value: 4,
        message: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.title_min_length`),
      },
      maxLength: {
        value: 60,
        message: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.message_max_length`),
      },
    },
    message: {
      required: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.input`),
      maxLength: {
        value: 1024,
        message: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.message_max_length`),
      },
    },
    header_select: {
      required: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.dropdown`),
    },
    header_text: {
      required: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.input`),
      maxLength: {
        value: 60,
        message: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.message_max_length`),
      },
      validate: (value: string) => {
        const hasNewline = /[\n\r]/.test(value);
        const hasAsterisk = /\*/.test(value);
        const hasEmoji = /\p{Emoji}/u.test(value);
        const hasFormatting = /[\t\v\f]/.test(value);
        if (hasNewline || hasAsterisk || hasEmoji || hasFormatting) {
          return t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.header_text_invalid`);
        }
        return true;
      },
    },
    media_url: {
      required: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.input`),
      pattern: {
        value: /^https?:\/\/.*\.(jpg|jpeg|png|pdf|mp4)$/i,
        message: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.media_format`),
      },
    },
    footer: {
      maxLength: {
        value: 60,
        message: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.message_max_length`),
      },
    },
    select_button_type: {
      required: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.dropdown`),
    },
    button_title: {
      required: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.input`),
      maxLength: {
        value: 20,
        message: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.message_max_length`),
      },
    },
    button_url: {
      required: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.input`),
      pattern: {
        value: /^(https?:\/\/)[a-zA-Z\d]+([-.][a-zA-Z\d]+)*\.[a-zA-Z]{2,}(:\d{1,5})?(\/.*)?$/,
        message: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.RULES.button_url_format`),
      },
    },
  };

  const getTemplateLanguage = (userLanguage?: string): string => {
    const normalizedLanguage = userLanguage?.toLowerCase();

    if (!normalizedLanguage) {
      return TEMPLATE_LANGUAGE.SPANISH;
    }

    return TEMPLATE_LANGUAGE_MAPPING[normalizedLanguage] || TEMPLATE_LANGUAGE.SPANISH;
  };

  /**
   * Extracts variables from a message using a defined pattern and prepares them for the template format
   * @param message - The message from which variables will be extracted
   * @returns An object with the processed variables and the modified content
   */
  const extractVariablesFromMessage = useCallback(
    // TODO: Extract in a modularize function in other file
    (
      message: string
    ): {
      variables: Record<string, string>;
      content: string;
    } => {
      const variableRegex = /\$\{\d+#[^}]+\}/g;
      const variables: Record<string, string> = {};
      let content = message;
      let index = 1;

      content = content.replace(variableRegex, (match) => {
        variables[index.toString()] = match;
        const replacement = `{{${index}}}`;
        index++;
        return replacement;
      });

      return { variables, content };
    },
    []
  );

  const handleDismissTemplate = useCallback(() => {
    reset();
    setTemplateOption(TEMPLATE_OPTION.IDLE);
  }, [reset, setTemplateOption]);

  const handleSubmitTemplate = useCallback(
    async (data: IWhatsAppTemplateFormV0) => {
      const { variables, content } = extractVariablesFromMessage(data.message);

      const template = {
        friendly_name: data.title,
        language: getTemplateLanguage(userData?.language),
        variables,
      };

      const templateData: {
        body: string;
        media?: string[];
        header_text?: string;
        footer?: string;
        actions: Array<{
          url?: string;
          title?: string;
          type?: string;
        }>;
      } = {
        body: content,
        actions: [],
      };

      let templateType = 'twilio/text';

      if (data.media_url && data.header_select?.id === 'media') {
        templateType = 'twilio/media';
        templateData.media = [data.media_url];
      }

      if (
        (data.header_text && data.header_select?.id === 'text') ||
        data.footer ||
        data.actions?.length > 0
      ) {
        templateType = 'whatsapp/card';

        if (data.header_select?.id === 'text') {
          templateData.header_text = data.header_text || '';
        }

        if (data.footer) {
          templateData.footer = data.footer;
        }

        if (data.actions && data.actions.length > 0) {
          data.actions.forEach((action) => {
            if (action.title && action.url) {
              templateData.actions.push({
                title: action.title,
                url: action.url,
                type: 'URL',
              });
            }
          });
        }
      }

      const payload: WhatsAppTemplateCreate = {
        ...template,
        types: {
          [templateType]: templateData,
        },
      };

      try {
        let response;

        switch (templateType) {
          case 'twilio/text':
            response = await inboxService.createTextTemplate(payload);
            break;
          case 'twilio/media':
            response = await inboxService.createMediaTemplate(payload);
            break;
          case 'whatsapp/card':
            response = await inboxService.createWhatsappCardTemplate(payload);
            break;
          default:
            console.error('Template type not supported:', templateType);
            break;
        }

        if (response?.status === 'received') {
          toast({
            title: t(
              `${TRANSLATE_WHATSAPP_ACTION_FORM}.TOAST_MESSAGES.createTemplate.success.title`
            ),
            body: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.TOAST_MESSAGES.createTemplate.success.body`),
            variant: 'success',
          });
          setIsTemplateCreated(true);
        }
      } catch (error) {
        toast({
          title: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.TOAST_MESSAGES.createTemplate.error.title`),
          body: t(`${TRANSLATE_WHATSAPP_ACTION_FORM}.TOAST_MESSAGES.createTemplate.error.body`),
          variant: 'error',
        });
        console.error('Error submitting template:', error);
      } finally {
        handleDismissTemplate();
      }
    },
    [
      userData,
      extractVariablesFromMessage,
      inboxService,
      handleDismissTemplate,
      setIsTemplateCreated,
      t,
    ]
  );

  return {
    control,
    handleSubmitTemplate: handleSubmit(handleSubmitTemplate),
    setValue,
    getValues,
    RULES,
    watch,
    handleDismissTemplate,
  };
};
